import React, { useState, useEffect } from 'react';
import TimeRange from './TimeRange';
import SubtitleAddForm from './SubtitleAddForm';
import WordAnnotateForm from './WordAnnotateForm';
import { round, formatTime } from './momentTime';
import { useTimeline } from './timelineContext';
import {
  nextStartAfter, subtitleFor, looseAnnotationsAt, windowOfAnnotations, endOf,
} from '../../utils/timeline';
import './SubtitlePanel.css';
import './AddPanel.css';

// The start follows playback this many seconds behind the current time, so
// pausing where a line is said frames it.
export const START_LEAD = 1;

export const startAt = (t) => Math.max(0, round(t - START_LEAD));

/**
 * AddPanel — always open on the annotate page. One time window (start → end)
 * for both the subtitle and the annotations at that point of the video: an
 * annotation's relevance is the subtitle showing then.
 *
 * The start follows the video (1 s before the current time). The end is
 * optional: left open, the subtitle and its annotations last until the next
 * subtitle starts, so the student page shows one at a time. Adjusting the
 * times fixes the window there, for the subtitle and all its words;
 * "Back to video time" lets it follow the video again.
 *
 * Subtitles and their words stay aligned whichever comes first: a word
 * annotated where a subtitle is takes that subtitle's times, and a subtitle
 * added where words were annotated first takes theirs (src/utils/timeline.js).
 *
 * Props:
 *   videoId, currentTime, language, yasna, yasnaError
 *   onSubtitleAdded(subtitle), onAnnotationSaved(annotation)
 *   moveControl — optional: the section's up / down arrows (AnnotatePage)
 */
function AddPanel({ videoId, currentTime, language, yasna, yasnaError, onSubtitleAdded, onAnnotationSaved, moveControl }) {
  const { subtitles, annotations } = useTimeline();
  const [start, setStart] = useState(() => startAt(currentTime));
  const [end, setEnd] = useState(null);
  const [fixed, setFixed] = useState(false);

  useEffect(() => {
    if (!fixed) setStart(startAt(currentTime));
  }, [currentTime, fixed]);

  const followVideo = () => {
    setFixed(false);
    setEnd(null);
  };

  // Another video: follow playback again.
  useEffect(followVideo, [videoId]);

  // The moment being annotated: the video's time, or the start as set.
  const at = fixed ? start : currentTime;
  const subtitleHere = subtitleFor(at, subtitles);
  const looseHere = looseAnnotationsAt(at, subtitles, annotations);
  const fromAnnotations = windowOfAnnotations(looseHere);
  // Words take the subtitle's times; a subtitle takes its earlier words'.
  const wordTimes = subtitleHere ? { start: subtitleHere.startTime, end: endOf(subtitleHere) } : { start, end };
  const subtitleTimes = fromAnnotations || { start, end };

  return (
    <section className="add-panel" aria-label="Add a subtitle and annotations">
      <div className="add-panel-head">
        <h3 className="add-panel-title">Add at this moment</h3>
        {fixed && (
          <button type="button" className="add-panel-follow" onClick={followVideo}
            title="The start time is fixed where you set it. Let it follow the video again.">
            Back to video time
          </button>
        )}
        {moveControl}
      </div>

      <TimeRange
        start={start}
        end={end}
        onChange={(w) => { setFixed(true); setStart(w.start); setEnd(w.end); }}
        currentTime={currentTime}
        optionalEnd
        nextStart={nextStartAfter(start, subtitles, annotations)}
        testIdPrefix="window"
      />

      {(subtitleHere || fromAnnotations) && (
        <p className="add-panel-align" data-testid="align-note">
          {subtitleHere
            ? `Words here align with the subtitle at ${formatTime(subtitleHere.startTime)}.`
            : `A subtitle here aligns with the ${looseHere.length === 1 ? 'word' : `${looseHere.length} words`} annotated at ${formatTime(fromAnnotations.start)}.`}
        </p>
      )}

      <SubtitleAddForm videoId={videoId} start={subtitleTimes.start} end={subtitleTimes.end} onAdded={onSubtitleAdded} />

      <div className="add-panel-divider" />

      <WordAnnotateForm
        videoId={videoId}
        language={language}
        yasna={yasna}
        yasnaError={yasnaError}
        start={wordTimes.start}
        end={wordTimes.end}
        onSaved={onAnnotationSaved}
      />
    </section>
  );
}

export default AddPanel;
